import { motion } from "framer-motion";
import { X, Wifi, Bluetooth, Volume2, Sun, Flashlight, Camera, Calculator, Timer } from "lucide-react";
import { useIOSStore } from "../hooks/useIOSStore";

interface ControlCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ControlCenter({ isOpen, onClose }: ControlCenterProps) {
  const { brightness, volume, setBrightness, setVolume } = useIOSStore();

  return (
    <motion.div
      initial={{ y: "-100%" }}
      animate={{ y: isOpen ? 0 : "-100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute inset-0 z-50 flex flex-col justify-between bg-black/80 backdrop-blur-xl p-6"
    >
      {/* Close Area */}
      <div className="flex justify-end">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="p-2 text-white/60 hover:text-white"
        >
          <X className="h-6 w-6" />
        </motion.button>
      </div>

      {/* Top Section - Connectivity */}
      <div className="grid grid-cols-2 gap-3">
        {/* Connectivity Group */}
        <div className="rounded-2xl bg-white/10 p-4">
          <div className="grid grid-cols-2 gap-2">
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="flex aspect-square items-center justify-center rounded-full bg-blue-500"
            >
              <Wifi className="h-6 w-6 text-white" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="flex aspect-square items-center justify-center rounded-full bg-white/20"
            >
              <Bluetooth className="h-6 w-6 text-white" />
            </motion.button>
          </div>
          <p className="mt-2 text-xs text-center text-white/60">Airplane Mode</p>
        </div>

        {/* Music - Placeholder */}
        <div className="rounded-2xl bg-white/10 p-4">
          <div className="flex h-full flex-col items-center justify-center">
            <div className="text-white/40 text-xs">Now Playing</div>
            <div className="mt-1 text-white text-sm font-medium">Not Playing</div>
          </div>
        </div>
      </div>

      {/* Middle Section - Sliders */}
      <div className="flex gap-3">
        {/* Brightness */}
        <div className="flex flex-1 items-center gap-3 rounded-2xl bg-white/10 p-3">
          <Sun className="h-5 w-5 text-white" />
          <input
            type="range"
            min="0"
            max="100"
            value={brightness}
            onChange={(e) => setBrightness(Number(e.target.value))}
            className="flex-1 h-8 appearance-none rounded-full bg-white/20 outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
          />
        </div>

        {/* Volume */}
        <div className="flex flex-1 items-center gap-3 rounded-2xl bg-white/10 p-3">
          <Volume2 className="h-5 w-5 text-white" />
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="flex-1 h-8 appearance-none rounded-full bg-white/20 outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
          />
        </div>
      </div>

      {/* Bottom Section - Quick Actions */}
      <div className="grid grid-cols-4 gap-3">
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="flex aspect-square items-center justify-center rounded-full bg-white/10"
        >
          <Flashlight className="h-6 w-6 text-white" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="flex aspect-square items-center justify-center rounded-full bg-white/10"
        >
          <Timer className="h-6 w-6 text-white" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="flex aspect-square items-center justify-center rounded-full bg-white/10"
        >
          <Calculator className="h-6 w-6 text-white" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="flex aspect-square items-center justify-center rounded-full bg-white/10"
        >
          <Camera className="h-6 w-6 text-white" />
        </motion.button>
      </div>
    </motion.div>
  );
}